// Re-export document template types from the shared package (single source of truth)
export type {
  DocumentTemplate,
  SignatureConfig,
  SignatureConfigTitle,
  SignatureConfigPerson,
} from '@mes-amis/super-form';

import type { DocumentTemplate } from '@mes-amis/super-form';

/**
 * Check if a template supports offline use (viewing/editing/creation).
 * Returns true if use_offline is true, undefined, or null (backward compatible).
 * Returns false only when use_offline is explicitly set to false.
 */
export function isTemplateOfflineEnabled(template: DocumentTemplate | null | undefined): boolean {
  if (!template) return true;
  return template.use_offline !== false;
}

/**
 * Whether a template opts out of prefilling. When true, new documents skip
 * automatic prefills and the Duplicate action is hidden, so assessors always
 * start fresh. Offline this gates iCode seeding (the only client-side prefill
 * path); config-defined `prefill:` values are computed server-side only.
 */
export function arePrefillsDisabled(template: DocumentTemplate | null | undefined): boolean {
  if (!template) return false;
  return template.disable_prefills === true;
}

/**
 * Whether a template accepts Duplicate-carried answers as-is: no confirmation
 * dots, no tint, nothing blocking Finish. Mirrors the monami server, which
 * removes the duplicated slugs from `field_prefills` entirely when the flag
 * is on (SuperForm::Table::Prefill#field_prefills).
 */
export function skipsPrefillConfirmation(template: DocumentTemplate | null | undefined): boolean {
  if (!template) return false;
  // Structural read until a @mes-amis/super-form release that adds the field
  // to the DocumentTemplate type is consumed here — then drop the cast.
  return (template as DocumentTemplate & { skip_prefill_confirmation?: boolean }).skip_prefill_confirmation === true;
}

export function isReviewEnabled(template: DocumentTemplate | null | undefined): boolean {
  if (!template) return false;
  return template.review_enabled === true;
}
